import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LightboxProvider } from "./components/Lightbox";
import { installBearerFetch } from "./lib/authToken";
import { startVersionPoller } from "./lib/versionPoller";

// Phase VV.63 — patch fetch once at module load so every subsequent
// API call carries the stored Bearer token. Fixes Capacitor APK cold-
// start "please log in" loop where the WebView drops the session cookie.
installBearerFetch();
// Phase VV.65 — backup auto-refresh via /api/version polling. Belt-and-
// braces alongside the service-worker `updatefound` path so mobile
// Safari + Capacitor WebView users still auto-reload on deploy.
startVersionPoller();

// Landing is eagerly imported because it's the first paint target — anything
// else lazy-loaded would create a brief blank flash on root load. AuthCallback
// is also eager because OAuth redirects need to handle session_id synchronously.
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";

// Code-split everything else. React.lazy splits each route into its own
// JS chunk that the browser only downloads when the user navigates there.
// Particularly important for `LiveRoomPage` + `Messages` which pull in the
// heavy Agora RTC SDK (~500KB gzipped) and the WebSocket transport, and for
// AdminCinema/AdminInquiries which most users will never visit.
const Register = lazy(() => import("./pages/Register"));
const Feed = lazy(() => import("./pages/Feed"));
const Profile = lazy(() => import("./pages/Profile"));
const PostPage = lazy(() => import("./pages/PostPage"));
const Messages = lazy(() => import("./pages/Messages"));
const TagPage = lazy(() => import("./pages/TagPage"));
const FollowList = lazy(() => import("./pages/FollowList"));
const LiveRoomPage = lazy(() => import("./pages/LiveRoomPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PromoteSuccess = lazy(() => import("./pages/PromoteSuccess"));
const Promote = lazy(() => import("./pages/Promote"));
const AdminInquiries = lazy(() => import("./pages/AdminInquiries"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const Cinema = lazy(() => import("./pages/Cinema"));
const AdminCinema = lazy(() => import("./pages/AdminCinema"));
const SharePage = lazy(() => import("./pages/SharePage"));
const BookmarkletPage = lazy(() => import("./pages/BookmarkletPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));

// Defer the listen-queue + PWA install prompt — they're not on the critical
// render path and dragging them off the main bundle shaves another chunk off
// first-load JS.
const ListenQueueBar = lazy(() => import("./components/ListenQueueBar"));
const InstallPrompt = lazy(() => import("./components/InstallPrompt"));

function RouteLoadingFallback() {
  // Minimal, non-distracting loading state — empty div keeps the layout
  // calm during the ~50-150ms chunk fetch. We deliberately avoid spinners
  // to prevent flashing for fast loads.
  return <div className="min-h-screen bg-slate-50" aria-busy="true" />;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback synchronously during render
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/register" element={<Register />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/u/:slug" element={<Profile />} />
        <Route path="/u/:slug/followers" element={<FollowList side="followers" />} />
        <Route path="/u/:slug/following" element={<FollowList side="following" />} />
        <Route path="/p/:post_id" element={<PostPage />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/g/:conv_id" element={<Messages />} />
        <Route path="/messages/:slug" element={<Messages />} />
        <Route path="/t/:tag" element={<TagPage />} />
        <Route path="/live/:room_id" element={<LiveRoomPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/promote" element={<Promote />} />
        <Route path="/promote/success" element={<PromoteSuccess />} />
        <Route path="/admin/inquiries" element={<AdminInquiries />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/cinema" element={<Cinema />} />
        <Route path="/admin/cinema" element={<AdminCinema />} />
        <Route path="/share" element={<SharePage />} />
        <Route path="/bookmarklet" element={<BookmarkletPage />} />
        <Route path="/videos" element={<VideosPage />} />
        {/* Legacy /shop URL — shop was removed Feb 2026. Redirect to feed
            so anyone with the old bookmark/PWA shortcut lands somewhere useful. */}
        <Route path="/shop" element={<Navigate to="/feed" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LightboxProvider>
          <AppRouter />
          <Suspense fallback={null}>
            <ListenQueueBar />
            <InstallPrompt />
          </Suspense>
        </LightboxProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
